import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import {
  Button,
  StandardDialog,
  createCurrencyField,
  createDateField,
  createSelectField,
  createTextField,
} from '@roc/ui';
import { LIEN_TYPE_OPTIONS } from '../store/lienFormStore';
import { useStore } from '@roc/client-portal-shared/hooks';
import { DateFormat, formatDate } from '@roc/feature-utils';

export interface AddEditLienModalProps {
  open: boolean;
  onClose: () => void;
  editingLienId?: number | null;
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  closeButton: {
    border: '1px solid #D0D0D0',
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
  },
}));

export const AddEditLienModal = observer(
  ({ open, onClose, editingLienId }: AddEditLienModalProps) => {
    const classes = useStyles();
    const { titleCommitmentStore } = useStore();
    const store = titleCommitmentStore.lienFormStore;
    const showPropertyField = titleCommitmentStore.loanProperties.length > 1;
    const propertyOptions = [
      { label: 'Select Property Address', value: '' },
      ...titleCommitmentStore.loanProperties.map(p => ({
        label: p.fullAddress,
        value: String(p.propertyId),
      })),
    ];

    const dialogContent = (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {createSelectField({
            store,
            fieldName: 'lienType',
            testId: 'lienType',
            label: 'Lien Type',
            options: LIEN_TYPE_OPTIONS,
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'madeBy',
            testId: 'madeBy',
            label: 'Borrower',
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'madeTo',
            testId: 'madeTo',
            label: 'Lender',
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'datedDate',
            testId: 'datedDate',
            label: 'Dated',
            disableFuture: true,
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'recordedDate',
            testId: 'recordedDate',
            label: 'Recorded',
            disableFuture: true,
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={12}>
          {createCurrencyField({
            store,
            fieldName: 'originalAmount',
            testId: 'originalAmount',
            label: 'Amount',
          })}
        </Grid>
        {showPropertyField && (
          <Grid item xs={12}>
            {createSelectField({
              store,
              fieldName: 'propertyId',
              testId: 'propertyId',
              label: 'Property Address',
              options: propertyOptions,
            })}
          </Grid>
        )}
      </Grid>
    );

    const handleSave = async () => {
      await titleCommitmentStore.saveLien(editingLienId);
      onClose();
    };

    const dialogActions = (
      <>
        <Button
          className={classes.closeButton}
          variant="outlined"
          onClick={onClose}
          testId="add-edit-lien-cancel"
        >
          Cancel
        </Button>
        <Button
          className={classes.saveButton}
          variant="contained"
          color="primary"
          onClick={handleSave}
          testId="add-edit-lien-save"
        >
          Save
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        title={`${!editingLienId ? 'Add' : 'Edit'} Lien`}
        maxWidth="sm"
        fullWidth
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        dialogContentclasses={{ root: classes.dialogContent }}
      />
    );
  }
);
